'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["className", "index", "message", "onToolCall", "part", "slots", "slotProps", "toolSlots", "toolSlotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChat } from "../../hooks/useChat.js";
import { useChatLocaleText } from "../../chat/internals/ChatLocaleContext.js";
import { formatStructuredValue } from "./partUtils.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function buildPreviewValue(formatted) {
  const firstLine = formatted.split('\n').find(line => line.trim().length > 0) ?? '';
  const trimmed = firstLine.trim();
  if (trimmed.length <= 60) {
    return trimmed;
  }
  return `${trimmed.slice(0, 60)}…`;
}
function ToolPayloadSection(props) {
  const {
    label,
    ownerState,
    section,
    slotProps,
    slots,
    value
  } = props;
  const formatted = React.useMemo(() => formatStructuredValue(value), [value]);
  const previewValue = React.useMemo(() => buildPreviewValue(formatted), [formatted]);
  const sectionOwnerState = React.useMemo(() => _extends({}, ownerState, {
    section,
    summaryLabel: label,
    previewValue
  }), [ownerState, section, label, previewValue]);
  const Section = slots?.section ?? 'div';
  const SectionSummary = slots?.sectionSummary ?? 'strong';
  const SectionContent = slots?.sectionContent ?? 'pre';
  const sectionProps = useSlotProps({
    elementType: Section,
    externalSlotProps: slotProps?.section,
    ownerState: sectionOwnerState
  });
  const sectionSummaryProps = useSlotProps({
    elementType: SectionSummary,
    externalSlotProps: slotProps?.sectionSummary,
    ownerState: sectionOwnerState
  });
  const sectionContentProps = useSlotProps({
    elementType: SectionContent,
    externalSlotProps: slotProps?.sectionContent,
    ownerState: sectionOwnerState
  });
  return /*#__PURE__*/_jsxs(Section, _extends({}, sectionProps, {
    children: [/*#__PURE__*/_jsx(SectionSummary, _extends({}, sectionSummaryProps, {
      children: label
    })), /*#__PURE__*/_jsx(SectionContent, _extends({}, sectionContentProps, {
      children: formatted
    }))]
  }));
}
export const ToolPartInner = /*#__PURE__*/React.forwardRef(function ToolPartRenderer(props, ref) {
  const {
    addToolApprovalResponse
  } = useChat();
  const {
      className,
      index,
      message,
      onToolCall,
      part,
      slots,
      slotProps,
      toolSlots,
      toolSlotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  void index;
  void onToolCall;
  const [pendingApproval, setPendingApproval] = React.useState(false);
  const localeText = useChatLocaleText();
  const toolName = part.toolInvocation.toolName;
  const resolvedSlots = React.useMemo(() => _extends({}, slots, toolSlots?.[toolName]), [slots, toolSlots, toolName]);
  const resolvedSlotProps = React.useMemo(() => _extends({}, slotProps, toolSlotProps?.[toolName]), [slotProps, toolSlotProps, toolName]);
  const ownerState = React.useMemo(() => ({
    messageId: message.id,
    pendingApproval,
    role: message.role,
    state: part.toolInvocation.state,
    toolName: part.toolInvocation.toolName
  }), [message.id, message.role, part.toolInvocation.state, part.toolInvocation.toolName, pendingApproval]);
  const Root = resolvedSlots?.root ?? 'div';
  const Header = resolvedSlots?.header ?? 'div';
  const Title = resolvedSlots?.title ?? 'div';
  const State = resolvedSlots?.state ?? 'span';
  const Icon = resolvedSlots?.icon;
  const Error = resolvedSlots?.error ?? 'div';
  const Actions = resolvedSlots?.actions ?? 'div';
  const ApproveButton = resolvedSlots?.approveButton ?? 'button';
  const DenyButton = resolvedSlots?.denyButton ?? 'button';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: resolvedSlotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref,
      className
    }
  });
  const headerProps = useSlotProps({
    elementType: Header,
    externalSlotProps: resolvedSlotProps?.header,
    ownerState
  });
  const titleProps = useSlotProps({
    elementType: Title,
    externalSlotProps: resolvedSlotProps?.title,
    ownerState
  });
  const stateProps = useSlotProps({
    elementType: State,
    externalSlotProps: resolvedSlotProps?.state,
    ownerState
  });
  // Always call unconditionally (hooks rule); only render when Icon slot is provided
  const iconProps = useSlotProps({
    elementType: Icon ?? 'span',
    externalSlotProps: resolvedSlotProps?.icon,
    ownerState
  });
  const errorProps = useSlotProps({
    elementType: Error,
    externalSlotProps: resolvedSlotProps?.error,
    ownerState
  });
  const actionsProps = useSlotProps({
    elementType: Actions,
    externalSlotProps: resolvedSlotProps?.actions,
    ownerState
  });
  const approveButtonProps = useSlotProps({
    elementType: ApproveButton,
    externalSlotProps: resolvedSlotProps?.approveButton,
    ownerState
  });
  const denyButtonProps = useSlotProps({
    elementType: DenyButton,
    externalSlotProps: resolvedSlotProps?.denyButton,
    ownerState
  });
  const {
    toolInvocation
  } = part;
  const toolTitle = toolInvocation.title ?? toolInvocation.toolName;
  const stateLabel = localeText.toolStateLabel(toolInvocation.state);
  const handleApproval = React.useCallback(async approved => {
    setPendingApproval(true);
    try {
      await addToolApprovalResponse({
        id: toolInvocation.toolCallId,
        approved
      });
    } catch {
      // Errors are surfaced through the chat runtime error channel.
    } finally {
      setPendingApproval(false);
    }
  }, [addToolApprovalResponse, toolInvocation.toolCallId]);
  const showInput = (toolInvocation.state === 'input-streaming' || toolInvocation.state === 'input-available' || toolInvocation.state === 'approval-requested' || toolInvocation.state === 'approval-responded' || toolInvocation.state === 'output-available' || toolInvocation.state === 'output-error') && toolInvocation.input !== undefined;
  const showOutput = toolInvocation.state === 'output-available' && toolInvocation.output !== undefined;
  return /*#__PURE__*/_jsxs(Root, _extends({}, rootProps, {
    children: [/*#__PURE__*/_jsxs(Header, _extends({}, headerProps, {
      children: [Icon != null ? /*#__PURE__*/_jsx(Icon, _extends({}, iconProps)) : null, /*#__PURE__*/_jsx(Title, _extends({}, titleProps, {
        children: toolTitle
      })), stateLabel ? /*#__PURE__*/_jsx(State, _extends({}, stateProps, {
        children: stateLabel
      })) : null]
    })), showInput ? /*#__PURE__*/_jsx(ToolPayloadSection, {
      label: localeText.messageToolInputLabel,
      ownerState: ownerState,
      section: "input",
      slotProps: resolvedSlotProps,
      slots: resolvedSlots,
      value: toolInvocation.input
    }) : null, showOutput ? /*#__PURE__*/_jsx(ToolPayloadSection, {
      label: localeText.messageToolOutputLabel,
      ownerState: ownerState,
      section: "output",
      slotProps: resolvedSlotProps,
      slots: resolvedSlots,
      value: toolInvocation.output
    }) : null, toolInvocation.state === 'output-error' && toolInvocation.errorText ? /*#__PURE__*/_jsx(Error, _extends({}, errorProps, {
      children: toolInvocation.errorText
    })) : null, toolInvocation.state === 'output-denied' ? /*#__PURE__*/_jsx(Error, _extends({}, errorProps, {
      children: toolInvocation.approval?.reason ?? localeText.toolStateLabel('output-denied')
    })) : null, toolInvocation.state === 'approval-requested' ? /*#__PURE__*/_jsxs(Actions, _extends({}, actionsProps, {
      children: [/*#__PURE__*/_jsx(ApproveButton, _extends({}, approveButtonProps, {
        disabled: pendingApproval,
        onClick: async event => {
          approveButtonProps.onClick?.(event);
          if (!event.defaultPrevented) {
            await handleApproval(true);
          }
        },
        type: "button",
        children: localeText.messageToolApproveButtonLabel
      })), /*#__PURE__*/_jsx(DenyButton, _extends({}, denyButtonProps, {
        disabled: pendingApproval,
        onClick: async event => {
          denyButtonProps.onClick?.(event);
          if (!event.defaultPrevented) {
            await handleApproval(false);
          }
        },
        type: "button",
        children: localeText.messageToolDenyButtonLabel
      }))]
    })) : null]
  }));
});

// Use a separate export name to avoid conflict with the ToolPart type alias
if (process.env.NODE_ENV !== "production") ToolPartInner.displayName = "ToolPartInner";
export { ToolPartInner as ToolPart };
export function createToolPartRenderer(defaultProps = {}) {
  return function ToolPartRendererFn(rendererProps) {
    return /*#__PURE__*/_jsx(ToolPartInner, _extends({}, defaultProps, rendererProps));
  };
}